/**
 * Raptor Predator Frontend - Error Fallback
 * 
 * Error boundary fallback component for graceful error handling.
 */

import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import type { FallbackProps } from 'react-error-boundary';

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps): JSX.Element {
  const isDevelopment = import.meta.env.DEV;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-900 p-6">
      <div className="w-full max-w-md rounded-2xl border border-red-500/30 bg-slate-800 p-8 text-center shadow-xl">
        {/* Error icon */}
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-500/20">
          <AlertTriangle className="h-8 w-8 text-red-500" />
        </div>

        {/* Title */}
        <h1 className="mb-2 text-xl font-bold text-white">Something went wrong</h1>
        
        {/* Description */}
        <p className="mb-6 text-sm text-slate-400">
          An unexpected error occurred. Our team has been notified.
        </p>

        {/* Error details (dev only) */}
        {isDevelopment && error instanceof Error && (
          <div className="mb-6 rounded-lg bg-slate-900 p-4 text-left">
            <p className="mb-2 font-mono text-xs text-red-400">{error.name}</p>
            <p className="font-mono text-xs text-slate-300">{error.message}</p>
            {error.stack != null && (
              <pre className="mt-2 max-h-32 overflow-auto font-mono text-xs text-slate-500">
                {error.stack}
              </pre>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={resetErrorBoundary}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-cyan-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-cyan-700"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
          <a
            href="/"
            className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-600 px-4 py-2.5 text-sm font-medium text-slate-300 transition-colors hover:bg-slate-700"
          >
            <Home className="h-4 w-4" />
            Go Home
          </a>
        </div>

        {/* Support link */}
        <p className="mt-6 text-xs text-slate-500">
          If this problem persists,{' '}
          <a href="mailto:support@raptorpredator.com" className="text-cyan-400 hover:underline">
            contact support
          </a>
        </p>
      </div>
    </div>
  );
}

export default ErrorFallback;

